import React, { useMemo, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, AlertTriangle, ArrowRight, Target, ChevronRight, Building2, MapPin, Globe, TrendingDown, Users, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { MOCK_RECOMMENDATIONS, Recommendation } from '../../data/mockRecommendationsData';
import { REGIONS, PROJECT_REGION_MAP } from '../../data/regionData';
import { RecommendationDetailModal } from './RecommendationDetailModal';
type Level = 'enterprise' | 'region' | 'project';
/** Highlights key data points in rationale text with bold + underline */
function formatInsightText(text: string): React.ReactNode {
  // Match: numbers with context (e.g. "6 fall-protection observations"), percentages, OSHA refs, day/week counts
  const pattern = /(\d+%|\d+\s+(?:fall-protection|ladder-use|struck-by|dust-related|scaffold|near[\s-]miss|lift plan|observation|inspection|PTP|cord|trench|mark-out|entry|worker|project|crew|item|site|case|day|hour|week|month|level|condition|discrepanc)\w*(?:\s+\w+){0,2})|(\b29\s*CFR\s*[\d.]+\b)|(\bOSHA\b(?:\s+\w+){0,3})|(\b(?:top|bottom)\s+decile\b)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(pattern);
  const globalRegex = new RegExp(pattern.source, 'gi');
  while ((match = globalRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<span key={match.index} className="font-semibold underline decoration-gray-300 underline-offset-2" data-id="element-2078" data-mp="bn7b0whrxvv3">
        {match[0]}
      </span>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}
export function RecommendationsView() {
  const [projectSearch, setProjectSearch] = useState('');
  const [currentLevel, setCurrentLevel] = useState<Level>('enterprise');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [expandedRecs, setExpandedRecs] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(3);
  const [expandedBUs, setExpandedBUs] = useState<Set<string>>(new Set());
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  // Recommendation counts for badges
  const enterpriseRecCount = useMemo(() => MOCK_RECOMMENDATIONS.filter(r => r.scope === 'systemic').length, []);
  const recCountsByRegion = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of REGIONS) {
      counts[r.id] = MOCK_RECOMMENDATIONS.filter(rec => rec.scope === 'region' && rec.regionId === r.id).length;
    }
    return counts;
  }, []);
  const recCountsByProject = useMemo(() => {
    const counts: Record<string, number> = {};
    // Calculate for all projects to show in the tree
    for (const p of PROJECT_REGION_MAP) {
      counts[p.projectName] = MOCK_RECOMMENDATIONS.filter(rec => rec.scope === 'project' && rec.affectedProjects.includes(p.projectName)).length;
    }
    return counts;
  }, []);
  const toggleExpand = (id: string) => {
    setExpandedRecs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleBUExpand = (regionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedBUs(prev => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);else next.add(regionId);
      return next;
    });
  };
  const filteredRecommendations = useMemo(() => {
    return MOCK_RECOMMENDATIONS.filter(rec => {
      // Level Filtering
      if (currentLevel === 'enterprise') {
        return rec.scope === 'systemic';
      }
      if (currentLevel === 'region') {
        return rec.scope === 'region' && rec.regionId === selectedRegion;
      }
      if (currentLevel === 'project') {
        return rec.scope === 'project' && rec.affectedProjects.includes(selectedProject!);
      }
      return true;
    }).sort((a, b) => {
      // Sort by risk reduction descending (highest impact first)
      return b.riskReduction - a.riskReduction;
    });
  }, [currentLevel, selectedRegion, selectedProject]);
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase tracking-wider" data-id="element-2079" data-mp="tqxvd79zcon5">
            <AlertTriangle size={10} data-id="element-2080" data-mp="3egnallelicgd" /> Critical
          </span>;
      case 'high':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wider" data-id="element-2081" data-mp="39eo3q1jncxeo">
            High
          </span>;
      case 'medium':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200 uppercase tracking-wider" data-id="element-2082" data-mp="1xyd2d3gzqfgz">
            Medium
          </span>;
      case 'low':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wider" data-id="element-2083" data-mp="2cnj16qbx23oc">
            Low
          </span>;
      default:
        return null;
    }
  };
  const handleEnterpriseClick = () => {
    setCurrentLevel('enterprise');
    setSelectedRegion(null);
    setSelectedProject(null);
    setVisibleCount(3);
  };
  const handleRegionClick = (regionId: string) => {
    setCurrentLevel('region');
    setSelectedRegion(regionId);
    setSelectedProject(null);
    setVisibleCount(3);
    // Auto-expand the BU when selected
    setExpandedBUs(prev => {
      const next = new Set(prev);
      next.add(regionId);
      return next;
    });
  };
  const handleProjectClick = (projectName: string, regionId: string) => {
    setCurrentLevel('project');
    setSelectedProject(projectName);
    setSelectedRegion(regionId);
    setVisibleCount(3);
  };
  const regionName = REGIONS.find(r => r.id === selectedRegion)?.name;
  return <div className="flex flex-row h-full bg-gray-50/50" data-id="element-2084" data-mp="28lmyw487sky0">
      {/* Left Sidebar Navigation */}
      <div className="w-[280px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full" data-id="element-2085" data-mp="2na106apzg1cg">
        <div className="p-4 border-b border-gray-100" data-id="element-2086" data-mp="2iaeo44lw4r58">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2087" data-mp="2id0w25cp18nz">
            Scope
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1 no-scrollbar" data-id="element-2088" data-mp="2rbuy4b8smr8x">
          {/* Enterprise Node */}
          <button onClick={handleEnterpriseClick} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors border-l-4 ${currentLevel === 'enterprise' ? 'bg-amber-50 text-amber-800 border-amber-500' : 'text-gray-700 hover:bg-gray-50 border-transparent'}`} data-id="element-2089" data-mp="3awv72mpfhcx0">
            <div className="flex items-center gap-2" data-id="element-2090" data-mp="3604ptw1w0ba0">
              <Globe size={16} className={currentLevel === 'enterprise' ? 'text-amber-600' : 'text-gray-400'} data-id="element-2091" data-mp="2s5lwsb7u2k4d" />
              Enterprise
            </div>
            <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold ${currentLevel === 'enterprise' ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2092" data-mp="2n7ajfqibxrhe">
              {enterpriseRecCount}
            </span>
          </button>

          {/* Business Units */}
          {REGIONS.map(region => {
          const isExpanded = expandedBUs.has(region.id);
          const isSelected = currentLevel === 'region' && selectedRegion === region.id;
          const regionProjectsList = PROJECT_REGION_MAP.filter(p => p.regionId === region.id).map(p => p.projectName).sort();
          return <div key={region.id} className="space-y-0.5 mt-1" data-id="element-2093" data-mp="2arug4arf1nx3">
                <button onClick={() => handleRegionClick(region.id)} className={`w-full flex items-center justify-between pr-3 py-2 rounded-lg text-sm font-medium transition-colors border-l-4 ${isSelected ? 'bg-amber-50 text-amber-800 border-amber-500' : 'text-gray-700 hover:bg-gray-50 border-transparent'}`} data-id="element-2094" data-mp="25xq6tkuoh3su">
                  <div className="flex items-center gap-1.5 pl-1" data-id="element-2095" data-mp="1mzx5gmzpd1xk">
                    <div onClick={e => toggleBUExpand(region.id, e)} className="p-1 rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-400 hover:text-gray-600" data-id="element-2096" data-mp="1n2t2m93q7mky">
                      {isExpanded ? <ChevronDown size={14} data-id="element-2097" data-mp="bgzu7imonseh" /> : <ChevronRight size={14} data-id="element-2098" data-mp="ghnku3702786" />}
                    </div>
                    <MapPin size={16} className={isSelected ? 'text-amber-600' : 'text-gray-400'} data-id="element-2099" data-mp="3fyvlwvr5ojso" />
                    <span className="truncate text-left" data-id="element-2100" data-mp="3owo4c8s6c7ka">{region.name}</span>
                  </div>
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold ${isSelected ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2101" data-mp="3tq0fbg3xkfuk">
                    {recCountsByRegion[region.id] ?? 0}
                  </span>
                </button>

                {/* Projects */}
                <AnimatePresence data-id="element-2102" data-mp="30od42lt9laly">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2103" data-mp="35qawa5jn7fr1">
                      <div className="pl-8 pr-2 py-1 space-y-0.5" data-id="element-2104" data-mp="2rqjledofafh9">
                        {regionProjectsList.map(project => {
                    const isProjectSelected = currentLevel === 'project' && selectedProject === project;
                    return <button key={project} onClick={() => handleProjectClick(project, region.id)} className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors border-l-2 ${isProjectSelected ? 'bg-amber-50 text-amber-800 font-medium border-amber-500' : 'text-gray-600 hover:bg-gray-50 border-transparent'}`} data-id="element-2105" data-mp="1rowkt3p35t23">
                              <div className="flex flex-col min-w-0" data-id="element-2106" data-mp="z0qzaeq7k017">
                                <div className="flex items-center gap-2 truncate" data-id="element-2107" data-mp="yywb8rjn3rsw">
                                  <Building2 size={14} className={`shrink-0 ${isProjectSelected ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-2108" data-mp="u3qt83tcyxa3" />
                                  <span className="truncate text-left" data-id="element-2109" data-mp="agdyf43uhh3o">
                                    {project}
                                  </span>
                                </div>
                                {(() => {
                          const mapping = PROJECT_REGION_MAP.find(p => p.projectName === project);
                          return mapping ? <span className="text-[10px] text-gray-400 ml-6 truncate text-left" data-id="element-2110" data-mp="u6hbisgntf79">
                                      {mapping.city}, {mapping.state}
                                    </span> : null;
                        })()}
                              </div>
                              <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold shrink-0 ${isProjectSelected ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'}`} data-id="element-2111" data-mp="1d2fq06gszevt">
                                {recCountsByProject[project] ?? 0}
                              </span>
                            </button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-2112" data-mp="148tisqlap94g">
        <div className="max-w-6xl mx-auto space-y-4" data-id="element-2113" data-mp="14aeggp2wp86j">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm mb-1" data-id="element-2114" data-mp="1i94fia9adhxj">
            <button onClick={handleEnterpriseClick} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${currentLevel === 'enterprise' ? 'font-bold text-amber-800 bg-amber-50' : 'font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`} data-id="element-2115" data-mp="1d7oq6602sp9c">
              <Globe size={14} data-id="element-2116" data-mp="y8ci7ke2q0yr" />
              Enterprise
            </button>
            {(currentLevel === 'region' || currentLevel === 'project') && regionName && <>
                  <ChevronRight size={14} className="text-gray-300" data-id="element-2117" data-mp="183nyi754rg8m" />
                  <button onClick={() => handleRegionClick(selectedRegion!)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${currentLevel === 'region' ? 'font-bold text-amber-800 bg-amber-50' : 'font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`} data-id="element-2118" data-mp="1c7ly0iydat27">
                    <MapPin size={14} data-id="element-2119" data-mp="1qxtami1p1huz" />
                    {regionName}
                  </button>
                </>}
            {currentLevel === 'project' && selectedProject && <>
                <ChevronRight size={14} className="text-gray-300" data-id="element-2120" data-mp="24uykkt4j30vi" />
                <span className="flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-amber-800 bg-amber-50" data-id="element-2121" data-mp="24t5dxdtx0gni">
                  <Building2 size={14} data-id="element-2122" data-mp="3ke5zh3hvaoqa" />
                  {selectedProject}
                </span>
              </>}
          </nav>

          <div className="mb-6" data-id="element-2123" data-mp="3gdm4a5gutv6s">
            {(() => {
            // Per-level styling
            const levelStyles = {
              enterprise: {
                bg: 'bg-indigo-50/70',
                border: 'border-indigo-200',
                label: 'ENTERPRISE',
                labelBg: 'bg-indigo-600',
                labelText: 'text-white',
                sparkle: 'text-indigo-500',
                textColor: 'text-indigo-900/80',
                accentBar: 'bg-indigo-500'
              },
              region: {
                bg: 'bg-teal-50/70',
                border: 'border-teal-200',
                label: 'BUSINESS UNIT',
                labelBg: 'bg-teal-600',
                labelText: 'text-white',
                sparkle: 'text-teal-500',
                textColor: 'text-teal-900/80',
                accentBar: 'bg-teal-500'
              },
              project: {
                bg: 'bg-amber-50/70',
                border: 'border-amber-200',
                label: 'PROJECT',
                labelBg: 'bg-amber-600',
                labelText: 'text-white',
                sparkle: 'text-amber-500',
                textColor: 'text-amber-900/80',
                accentBar: 'bg-amber-500'
              }
            };
            // Per-region color accents for within-level distinction
            const regionAccents: Record<string, {
              ring: string;
              nameBg: string;
              nameText: string;
            }> = {
              northeast: {
                ring: 'ring-blue-300',
                nameBg: 'bg-blue-100',
                nameText: 'text-blue-800'
              },
              mid_atlantic: {
                ring: 'ring-violet-300',
                nameBg: 'bg-violet-100',
                nameText: 'text-violet-800'
              },
              southeast: {
                ring: 'ring-rose-300',
                nameBg: 'bg-rose-100',
                nameText: 'text-rose-800'
              },
              midwest: {
                ring: 'ring-emerald-300',
                nameBg: 'bg-emerald-100',
                nameText: 'text-emerald-800'
              },
              southwest: {
                ring: 'ring-orange-300',
                nameBg: 'bg-orange-100',
                nameText: 'text-orange-800'
              },
              west_coast: {
                ring: 'ring-cyan-300',
                nameBg: 'bg-cyan-100',
                nameText: 'text-cyan-800'
              }
            };
            const style = levelStyles[currentLevel];
            const regionAccent = selectedRegion ? regionAccents[selectedRegion] || regionAccents.northeast : null;
            const projectRegionId = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.regionId : null;
            const projectRegionAccent = projectRegionId ? regionAccents[projectRegionId] || null : null;
            const projectCity = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.city : null;
            const projectState = selectedProject ? PROJECT_REGION_MAP.find(p => p.projectName === selectedProject)?.state : null;
            return <div className={`relative overflow-hidden rounded-xl border ${style.border} ${style.bg}`} data-id="element-2124" data-mp="3ft0v0hr4qqpv">
                  {/* Accent bar on left */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.accentBar}`} data-id="element-2125" data-mp="z8x8n1paj5a4" />

                  <div className="pl-5 pr-4 py-4" data-id="element-2126" data-mp="ufcg3p6gv3ib">
                    {/* Top row: level label + unit name */}
                    <div className="flex items-center gap-2.5 mb-2" data-id="element-2127" data-mp="pg89n6axfjvo">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${style.labelBg} ${style.labelText}`} data-id="element-2128" data-mp="li26075dxkez">
                        {style.label}
                      </span>
                      {currentLevel === 'enterprise' && <span className="text-base font-bold text-gray-900" data-id="element-2129" data-mp="6l1bxwzq1ddc">
                          All Projects
                        </span>}
                      {currentLevel === 'region' && regionName && regionAccent && <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-sm font-bold ring-1 ${regionAccent.ring} ${regionAccent.nameBg} ${regionAccent.nameText}`} data-id="element-2130" data-mp="2u9wyrno7p74f">
                            <MapPin size={13} data-id="element-2131" data-mp="2gh79naddzm33" />
                            {regionName}
                          </span>}
                      {currentLevel === 'project' && selectedProject && <div className="flex items-center gap-2" data-id="element-2132" data-mp="26hgdl496gz0w">
                          <span className="text-base font-bold text-gray-900" data-id="element-2133" data-mp="21ilojkkmae8c">
                            {selectedProject}
                          </span>
                          {projectCity && projectState && <span className="text-xs text-gray-500" data-id="element-2134" data-mp="3vw4pz5hrqgyx">
                              {projectCity}, {projectState}
                            </span>}
                          {projectRegionAccent && regionName && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${projectRegionAccent.ring} ${projectRegionAccent.nameBg} ${projectRegionAccent.nameText}`} data-id="element-2135" data-mp="1imwja6328si7">
                              <MapPin size={10} data-id="element-2136" data-mp="1rh168ef2pjsg" />
                              {regionName}
                            </span>}
                        </div>}
                    </div>

                    {/* AI message */}
                    <div className="flex items-start gap-2" data-id="element-2137" data-mp="13o2mgrpwmldu">
                      <Sparkles size={13} className={`${style.sparkle} mt-0.5 shrink-0`} data-id="element-2138" data-mp="18p8h4v34kiq0" />
                      <p className={`text-sm ${style.textColor}`} data-id="element-2139" data-mp="2f75amo035nki">
                        {currentLevel === 'enterprise' ? <>
                            Across your entire portfolio of{' '}
                            <span className="font-semibold" data-id="element-2140" data-mp="g0avrvh10jl4">
                              {PROJECT_REGION_MAP.length} projects
                            </span>
                            , I identified systemic patterns in risk trends,
                            observations, and compliance data — here's what
                            stands out.
                          </> : currentLevel === 'region' ? <>
                            Across the{' '}
                            <span className="font-semibold" data-id="element-2141" data-mp="b17qqb11qdd5">
                              {PROJECT_REGION_MAP.filter(p => p.regionId === selectedRegion).length}{' '}
                              projects
                            </span>{' '}
                            in this business unit, I identified regional
                            patterns in risk trends and observations — here's
                            what stands out.
                          </> : <>
                            I reviewed recent risk trends, observations, and
                            inspection findings for this project — here's what I
                            recommend.
                          </>}
                      </p>
                    </div>
                  </div>
                </div>;
          })()}
          </div>

          {filteredRecommendations.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-2142" data-mp="23wqx5vdoncdp">
              <Target size={32} className="mx-auto text-gray-300 mb-3" data-id="element-2143" data-mp="njad7vkxg11u" />
              <h3 className="text-gray-900 font-medium" data-id="element-2144" data-mp="sidc0yabdb1d">
                No recommendations found
              </h3>
              <p className="text-gray-500 text-sm mt-1" data-id="element-2145" data-mp="17sjqm2twmk1x">
                {currentLevel === 'project' ? 'This project currently has no specific recommendations.' : 'No recommendations available at this level.'}
              </p>
            </div> : <div className="space-y-4" data-id="element-2146" data-mp="u1evne1hefxx">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-2147" data-mp="u2q1djehlxro">
                <table className="w-full text-left text-sm" data-id="element-2148" data-mp="1qqcsd1ssknxq">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2149" data-mp="1lrt6t2i41e5w">
                    <tr data-id="element-2150" data-mp="1hq7lcsrifrk3">
                      <th className="px-4 py-3 font-bold text-gray-900 w-1/4" data-id="element-2151" data-mp="1hpenkhaj4ce3">
                        Insight
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-1/4" data-id="element-2152" data-mp="1cr3a7wl0zjr4">
                        Corrective Action
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 text-center w-28" data-id="element-2153" data-mp="1boesx8gszy4">
                        Risk Reduction
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-36" data-id="element-2154" data-mp="2cxgmnp92dmpb">
                        Affected
                      </th>
                      <th className="px-4 py-3 font-bold text-gray-900 w-48" data-id="element-2155" data-mp="2lzpsw2xautww">
                        Linked Drivers
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-2156" data-mp="2vqazacj7t2qd">
                    {filteredRecommendations.slice(0, visibleCount).map(rec => <Fragment key={rec.id} data-id="element-2157" data-mp="35oi6w4rm68fp">
                          <tr className="hover:bg-gray-50 transition-colors group" data-id="element-2158" data-mp="35oqhfq09l1nq">
                            {/* Insight (rationale + trend) */}
                            <td className="px-4 py-4 align-top" data-id="element-2159" data-mp="1bah7nis5jjf3">
                              <div className="text-xs text-gray-600 leading-relaxed" data-id="element-2160" data-mp="1bcagn4q8mfq9">
                                {formatInsightText(rec.rationale)}
                              </div>
                            </td>
                            {/* Recommendation */}
                            <td className="px-4 py-4 align-top" data-id="element-2161" data-mp="2dox5h3au1smz">
                              <div className="font-bold text-gray-900 mb-1" data-id="element-2162" data-mp="2rnwkdj8jmt5y">
                                {rec.title}
                              </div>
                              <div className="mt-2" data-id="element-2163" data-mp="2nnw741x6ko0h">
                                <button onClick={() => setSelectedRecommendation(rec)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-900 bg-amber-400 hover:bg-amber-500 px-2.5 py-1 rounded-md shadow-sm transition-colors" data-id="element-2164" data-mp="31ksufte3bncm">
                                  <Sparkles size={11} data-id="element-2165" data-mp="36encythylwa4" />
                                  Discuss with Bilti
                                </button>
                              </div>
                            </td>
                            {/* Risk Reduction */}
                            <td className="px-4 py-4 align-top text-center" data-id="element-2166" data-mp="2twmaol6x6yt">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 w-fit" data-id="element-2167" data-mp="7rew0h717q7v">
                                <TrendingDown size={12} data-id="element-2168" data-mp="3tz4ysjgcohde" />-{rec.riskReduction}%
                                Risk
                              </span>
                            </td>
                            {/* Affected */}
                            <td className="px-4 py-4 align-top" data-id="element-2169" data-mp="3kzro5nghpmr3">
                              <div className="flex flex-col gap-1.5" data-id="element-2170" data-mp="36alro791eamw">
                                {(rec.scope === 'systemic' || rec.scope === 'region') && rec.projectsAffected ? (() => {
                          const projects = rec.affectedProjects.includes('All Projects') ? PROJECT_REGION_MAP.map(p => p.projectName) : rec.affectedProjects;
                          const buBreakdown: Record<string, string[]> = {};
                          for (const proj of projects) {
                            const mapping = PROJECT_REGION_MAP.find(p => p.projectName === proj);
                            const buName = mapping ? REGIONS.find(r => r.id === mapping.regionId)?.name || mapping.regionId : 'Unknown';
                            if (!buBreakdown[buName]) buBreakdown[buName] = [];
                            buBreakdown[buName].push(proj);
                          }
                          const buCount = Object.keys(buBreakdown).length;
                          const tooltipLines = Object.entries(buBreakdown).sort((a, b) => b[1].length - a[1].length).map(([bu, projs]) => `${bu}: ${projs.length} project${projs.length !== 1 ? 's' : ''}`).join('\n');
                          const buEntries = Object.entries(buBreakdown).sort((a, b) => b[1].length - a[1].length);
                          return <div className="relative group/bu w-fit" data-id="element-2171" data-mp="2uyibzaoeo9ki">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 w-fit cursor-default" data-id="element-2172" data-mp="2v0mnoesjqcvc">
                                          <Building2 size={10} data-id="element-2173" data-mp="2l26xce1cf7mw" />
                                          {rec.projectsAffected} projects ·{' '}
                                          {buCount} BU{buCount !== 1 ? 's' : ''}
                                        </span>
                                        <div className="absolute left-0 bottom-full mb-1.5 z-50 hidden group-hover/bu:block" data-id="element-2174" data-mp="1xb9udqrizkns">
                                          <div className="bg-gray-900 text-white text-[10px] rounded-lg px-3 py-2 shadow-lg whitespace-nowrap space-y-0.5" data-id="element-2175" data-mp="22bp84jfpyu6a">
                                            <div className="font-bold text-gray-300 mb-1" data-id="element-2176" data-mp="1dq9ojo66tf3x">
                                              Projects by Business Unit
                                            </div>
                                            {buEntries.map(([bu, projs]) => <div key={bu} className="flex items-center justify-between gap-4" data-id="element-2177" data-mp="21lvlym8r4ulr">
                                                <span className="text-gray-200" data-id="element-2178" data-mp="26dnciwrwr0bw">
                                                  {bu}
                                                </span>
                                                <span className="font-bold text-white" data-id="element-2179" data-mp="2kb25gbhddq8m">
                                                  {projs.length}
                                                </span>
                                              </div>)}
                                          </div>
                                        </div>
                                      </div>;
                        })() : rec.contractorsAffected ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 w-fit" data-id="element-2180" data-mp="36599y8277yvl">
                                    <Users size={10} data-id="element-2181" data-mp="3g50428mukbv5" />
                                    {rec.contractorsAffected} contractors
                                  </span> : null}
                              </div>
                            </td>
                            {/* Linked Drivers */}
                            <td className="px-4 py-4 align-top" data-id="element-2182" data-mp="1qqv10zpuc9zj">
                              <div className="flex flex-wrap gap-1.5" data-id="element-2183" data-mp="7ufqg136aovb">
                                {rec.linkedDrivers.map(driver => <span key={driver} className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded" data-id="element-2184" data-mp="31309miuia0g">
                                    {driver}
                                  </span>)}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Row */}
                          <AnimatePresence data-id="element-2185" data-mp="ctijso9sj6ng">
                            {expandedRecs.has(rec.id) && rec.activityExamples && <tr className="bg-gray-50 border-b border-gray-200" data-id="element-2186" data-mp="32fomrvhp16t8">
                                  <td colSpan={5} className="p-0" data-id="element-2187" data-mp="2xl16ul0r9kq9">
                                    <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} className="overflow-hidden" data-id="element-2188" data-mp="p9glb66xfyib">
                                      <div className="px-6 py-4" data-id="element-2189" data-mp="1418zheuzj6ig">
                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-2190" data-mp="3jpito0xfanta">
                                          Affected Activity Examples
                                        </h4>
                                        <div className="space-y-2" data-id="element-2191" data-mp="3h7yvpog1x4su">
                                          {rec.activityExamples.map((ex, idx) => <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-4 shadow-sm" data-id="element-2192" data-mp="3q4g6gqki70o2" data-mpi={idx}>
                                                <div className="flex-1 min-w-0" data-id="element-2193" data-mp="2te722rwmn0rv">
                                                  <div className="flex items-center gap-2 mb-1" data-id="element-2194" data-mp="378zkpz7tcx2o">
                                                    <span className="text-sm font-bold text-gray-900 truncate" data-id="element-2195" data-mp="32fn9qrw24ose">
                                                      {ex.activityName}
                                                    </span>
                                                    {rec.scope !== 'project' && <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded truncate" data-id="element-2196" data-mp="imxzctcsnb93">
                                                        {ex.projectName}
                                                      </span>}
                                                  </div>
                                                  <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-2197" data-mp="3vvk8wyt8e6me">
                                                    <span className="flex items-center gap-1" data-id="element-2198" data-mp="4mwp4nan2rgy">
                                                      <Users size={12} data-id="element-2199" data-mp="8rmju8tka00a" />{' '}
                                                      {ex.contractor}
                                                    </span>
                                                    <span className="flex items-center gap-1" data-id="element-2200" data-mp="1k3ey22hryv80">
                                                      <MapPin size={12} data-id="element-2201" data-mp="34mr6p4l19aay" />{' '}
                                                      {ex.floor}
                                                    </span>
                                                  </div>
                                                  <p className="text-xs text-gray-600 mt-2" data-id="element-2202" data-mp="2qwdz53c1zxm5">
                                                    {ex.explanation}
                                                  </p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-2203" data-mp="2h0133yt1d7o8">
                                                  <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${ex.currentRisk === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : ex.currentRisk === 'high' ? 'bg-amber-50 text-amber-700 border-amber-200' : ex.currentRisk === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-2204" data-mp="27ybc2u32gaqz">
                                                    {ex.currentRisk}
                                                  </div>
                                                  <ArrowRight size={14} className="text-gray-400" data-id="element-2205" data-mp="2sffiapw0wgoi" />
                                                  <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${ex.riskAfter === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : ex.riskAfter === 'high' ? 'bg-amber-50 text-amber-700 border-amber-200' : ex.riskAfter === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-2206" data-mp="2iiknoi2vf0fi">
                                                    {ex.riskAfter}
                                                  </div>
                                                </div>
                                              </div>)}
                                        </div>
                                      </div>
                                    </motion.div>
                                  </td>
                                </tr>}
                          </AnimatePresence>
                        </Fragment>)}
                  </tbody>
                </table>
              </div>

              {visibleCount < filteredRecommendations.length && <div className="pt-4 flex justify-center" data-id="element-2207" data-mp="zkeld3v27sdt">
                  <button onClick={() => setVisibleCount(prev => prev + 3)} className="px-6 py-2.5 bg-white border border-amber-200 text-amber-700 text-sm font-bold rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors shadow-sm" data-id="element-2208" data-mp="20jpms47qaz0z">
                    Show 3 More ({filteredRecommendations.length - visibleCount}{' '}
                    remaining)
                  </button>
                </div>}
            </div>}
        </div>
      </div>

      <RecommendationDetailModal recommendation={selectedRecommendation} onClose={() => setSelectedRecommendation(null)} data-id="element-2209" data-mp="2egcwz8oozlou" />
    </div>;
}